import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import {
  markResolved,
  sendReply,
  subscribeSupportTickets,
  updateTicketDraft,
} from "../../services/supportTicketsService";
import "./AdminSupportInbox.css";

const FILTERS = ["open", "high", "resolved", "all"];

function shortTime(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function formatDetailTime(ts) {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}

function PriorityPill({ priority }) {
  const p = (priority || "normal").toLowerCase();
  if (p === "high") {
    return (
      <span className="admin-priority-pill admin-priority-high">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        High Priority
      </span>
    );
  }
  if (p === "low") {
    return <span className="admin-priority-pill admin-priority-low">Low Priority</span>;
  }
  return <span className="admin-priority-pill admin-priority-normal">Normal</span>;
}

export default function AdminSupportInbox() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [editingDraft, setEditingDraft] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    return subscribeSupportTickets((data) => {
      setTickets(data);
      setSelectedId((prev) => {
        if (prev && data.some((t) => t.id === prev)) return prev;
        return data[0]?.id || null;
      });
    });
  }, []);

  const filteredTickets = useMemo(() => {
    let list = [...tickets];
    if (filter === "open") list = list.filter((t) => t.status !== "resolved");
    else if (filter === "high") list = list.filter((t) => (t.priority || "").toLowerCase() === "high" && t.status !== "resolved");
    else if (filter === "resolved") list = list.filter((t) => t.status === "resolved");

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (t) =>
          (t.name || "").toLowerCase().includes(q) ||
          (t.email || "").toLowerCase().includes(q) ||
          (t.subject || "").toLowerCase().includes(q) ||
          (t.message || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [tickets, filter, search]);

  const selected = filteredTickets.find((t) => t.id === selectedId)
    || tickets.find((t) => t.id === selectedId)
    || null;

  useEffect(() => {
    if (selected && !editingDraft) {
      setDraftText(selected.aiDraftReply || "");
    }
  }, [selected, editingDraft]);

  const stats = useMemo(() => {
    const open = tickets.filter((t) => t.status !== "resolved").length;
    const high = tickets.filter((t) => (t.priority || "").toLowerCase() === "high" && t.status !== "resolved").length;
    return { open, high };
  }, [tickets]);

  const handleSendReply = async () => {
    if (!selected) {
      setError("Select a ticket to review before sending.");
      return;
    }
    const replyText = draftText.trim();
    if (!replyText) {
      setError("Draft reply cannot be empty.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await sendReply({ ticketId: selected.id, replyText });
      setEditingDraft(false);
    } catch (err) {
      setError(err.message || "Send failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await updateTicketDraft(selected.id, draftText);
      setEditingDraft(false);
    } catch (err) {
      setError(err.message || "Save draft failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleMarkResolved = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await markResolved(selected.id);
    } catch (err) {
      setError(err.message || "Mark resolved failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-support-shell">
        <div className="admin-support-header-row">
          <div className="admin-page-header">
            <div className="admin-eyebrow">Support</div>
            <div className="admin-page-title">Support <em>Inbox</em></div>
            <span className="admin-page-hand">— real replies, not form letters</span>
          </div>
          <div className="admin-stat-strip">
            <div className="admin-stat">
              <div className="admin-stat-num red">{stats.high}</div>
              <div className="admin-stat-label">High Priority</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat-num">{stats.open}</div>
              <div className="admin-stat-label">Open</div>
            </div>
          </div>
        </div>

        <div className="admin-support-toolbar">
          <div className="admin-filter-pills">
            {FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                className={`admin-pill${filter === key ? " active" : ""}${key === "high" ? " high-pill" : ""}`}
                onClick={() => setFilter(key)}
              >
                {key === "high" && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                )}
                {key === "open" ? "Open" : key === "high" ? "High Priority" : key === "resolved" ? "Resolved" : "All"}
              </button>
            ))}
          </div>
          <div className="admin-search-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Search tickets"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className={`admin-inbox-shell${selected ? " show-detail" : ""}`}>
          <div className="admin-inbox-list">
            {filteredTickets.length === 0 ? (
              <div className="admin-inbox-detail-empty" style={{ padding: 24 }}>No tickets match this filter.</div>
            ) : (
              filteredTickets.map((ticket) => {
                const initial = (ticket.name || ticket.email || "?").charAt(0).toUpperCase();
                const priority = (ticket.priority || "normal").toLowerCase();
                return (
                  <button
                    key={ticket.id}
                    type="button"
                    className={`admin-inbox-row${selectedId === ticket.id ? " selected" : ""}`}
                    onClick={() => {
                      setSelectedId(ticket.id);
                      setEditingDraft(false);
                      setError("");
                    }}
                  >
                    <div className={`admin-ir-priority-dot ${priority}`} />
                    <div className="admin-ir-avatar">{initial}</div>
                    <div className="admin-ir-body">
                      <div className="admin-ir-top-row">
                        <span className="admin-ir-name">{ticket.name || ticket.email}</span>
                        <span className="admin-ir-time">{shortTime(ticket.createdAt)}</span>
                      </div>
                      <div className="admin-ir-subject">{ticket.subject || "(No subject)"}</div>
                      <div className="admin-ir-preview">{ticket.message || ""}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="admin-inbox-detail">
            {!selected ? (
              <div className="admin-inbox-detail-empty">Select a ticket to review.</div>
            ) : (
              <>
                <div className="admin-detail-header">
                  <div className="admin-detail-from">
                    <div className="admin-dh-avatar">
                      {(selected.name || selected.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="admin-dh-name">{selected.name || selected.email}</div>
                      <div className="admin-dh-meta">
                        {selected.email}
                        {selected.role ? ` · ${selected.role}` : ""}
                        {selected.createdAt ? ` · ${formatDetailTime(selected.createdAt)}` : ""}
                      </div>
                    </div>
                  </div>
                  <PriorityPill priority={selected.priority} />
                </div>

                {selected.category && (
                  <div className="admin-category-tag">{selected.category}</div>
                )}

                <div className="admin-ticket-subject">{selected.subject || "(No subject)"}</div>
                <div className="admin-ticket-preview">
                  &ldquo;{selected.message || ""}&rdquo;
                </div>

                <div className="admin-ai-draft">
                  <div className="admin-ai-draft-label">✦ AI Suggested Reply</div>
                  {editingDraft ? (
                    <textarea
                      className="admin-ai-draft-textarea"
                      value={draftText}
                      onChange={(e) => setDraftText(e.target.value)}
                    />
                  ) : (
                    <div className="admin-ai-draft-text">
                      {draftText || "No draft yet — click Edit Draft to write one."}
                    </div>
                  )}
                </div>

                {error && <div className="admin-support-error">{error}</div>}

                <div className="admin-ticket-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn-niche"
                    onClick={handleSendReply}
                    disabled={busy || selected.status === "resolved"}
                  >
                    Send Reply
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => {
                      if (editingDraft) handleSaveDraft();
                      else setEditingDraft(true);
                    }}
                    disabled={busy}
                  >
                    {editingDraft ? "Save Draft" : "Edit Draft"}
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    onClick={handleMarkResolved}
                    disabled={busy || selected.status === "resolved"}
                  >
                    Mark Resolved
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
